import { useRouteParams } from "@vueuse/router";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import {
  useCurrentExperienceLocationQuery,
  EXPERIENCE_LOCATION_QUERY_KEY,
} from "@/features/experience-shared/queries/useExperienceLocationQuery";
import { buildLocationPayload } from "@/features/experience-shared/utils/address-utils";
import { useCurationCity } from "@/features/experience-curation/sections/location/city/useCurationCity";
import { useCurationAddress } from "@/features/experience-curation/sections/location/address/useCurationAddress";
import type { ExperienceLocation } from "@/types/generated/MetadataExperiencesApi";

export function useSaveLocationMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const metadataService = useMetadataExperienceApi();
  const locationQuery = useCurrentExperienceLocationQuery();
  const cityStore = useCurationCity();
  const addressStore = useCurationAddress();

  return useMutation({
    mutationFn: async () => {
      if (!cityStore.hasChanges && !addressStore.hasChanges) return;

      const existingId = locationQuery.data.value?.id;
      const locationPayload = buildLocationPayload(cityStore.model, addressStore.address, addressStore.locationType);

      if (!locationPayload && existingId) {
        await metadataService.deleteLocation(existingId);
        return;
      }

      if (!locationPayload) return;

      const payload: ExperienceLocation = {
        experience_id: experienceId.value,
        ...locationPayload,
      };

      if (existingId) {
        await metadataService.updateLocation(existingId, payload);
      } else {
        await metadataService.createLocation(payload);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERIENCE_LOCATION_QUERY_KEY] }),
  });
}
